'use client';

import { forwardRef, useState } from 'react';
import { Loading } from '@/app/components/icons/Loading';
import { HOVER_PLAY_DELAY } from '@/app/constants';
import { useDevice, useHoverPlayVideo, useMediaMetrics, useVideoCompletion } from '@/app/hooks';
import { cn, reportEvent } from '@/app/utils';
import { usePathname } from '@/i18n/routing';

export interface HoverPlayMediaProps {
  /** 媒体项ID */
  id: string;
  /** 视频URL */
  uri?: string;
  /** 封面图URL */
  coverUri?: string;
  /** 点击处理函数 */
  onClick?: (id: string) => void;
  /** 自定义类名 */
  className?: string;
  wrapperCls?: string;
  /** 视频类名 */
  videoClassName?: string;
  /** 图片类名 */
  imageClassName?: string;
  /** 子元素 */
  children?: React.ReactNode;
  footer?: React.ReactNode;
  /** 宽高比，默认为16/9 */
  aspectRatio?: number | string;
  /** 鼠标悬停开始播放回调 */
  onPlayStart?: () => void;
  /** 事件名称 */
  eventName: string;
  /** 自定义鼠标悬停开始播放回调 */
  onMouseEnter?: () => void;
  /** 自定义鼠标离开停止播放回调 */
  onMouseLeave?: () => void;
  /** 事件属性 */
  eventProperties?: Record<string, string | boolean | number>;
  borderRadiusClassName?: string;
  /** 是否显示模糊背景图 */
  showMaskBg?: boolean;
  showLoading?: boolean;
  /** 是否静音 */
  muted?: boolean;
  /** 封面图 alt 文本 */
  alt?: string;
}

const HoverPlayMedia = forwardRef<HTMLDivElement, HoverPlayMediaProps>(
  (
    {
      id,
      uri,
      coverUri,
      onClick,
      className = '',
      videoClassName = '',
      imageClassName = '',
      wrapperCls,
      children,
      aspectRatio = 16 / 9,
      onPlayStart,
      onMouseEnter,
      onMouseLeave,
      eventName,
      eventProperties,
      footer,
      borderRadiusClassName = 'rounded-8',
      showMaskBg,
      showLoading,
      muted = true,
      alt = 'AI generated video',
    },
    ref,
  ) => {
    const pathname = usePathname();
    const [isLoading, setIsLoading] = useState(true);
    const { isMobile } = useDevice();
    const {
      handleEnter: handleMouseEnterTracker,
      handleLeave: handleMouseLeaveTracker,
      incrementCompleteTimes,
    } = useMediaMetrics({
      delay: 2000,
      callback(detailStayTime, completeTimes) {
        reportEvent(eventName, {
          interaction_type: 'hoverplay',
          interaction_position: pathname,
          ...eventProperties,
          hover_staytime: detailStayTime,
          complete_times: completeTimes,
          content_type: 'video',
        });
      },
    });

    const { handleTimeUpdate } = useVideoCompletion(incrementCompleteTimes);
    const { videoRef, handleMouseEnter, handleMouseLeave } = useHoverPlayVideo({
      delay: HOVER_PLAY_DELAY,
      disabled: isMobile,
      onMouseEnter: () => {
        handleMouseEnterTracker();
        onMouseEnter?.();
      },
      onMouseLeave: () => {
        onMouseLeave?.();
        handleMouseLeaveTracker();
      },
      onPlayStart,
    });

    // 处理媒体加载完成
    const handleMediaLoaded = () => {
      setIsLoading(false);
    };

    // 处理点击事件
    const handleClick = () => {
      onClick?.(id);
    };

    return (
      <div
        className={cn('cursor-pointer', className)}
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        ref={ref}
      >
        <div
          className={cn('relative h-full w-full overflow-hidden', wrapperCls)}
          style={{
            aspectRatio,
          }}
        >
          {showMaskBg && (
            <div
              className={cn(
                'absolute top-0 right-0 bottom-0 left-0 z-[-1] h-full w-full overflow-hidden',
                borderRadiusClassName,
              )}
            >
              <img
                src={coverUri}
                alt={alt}
                className="h-full w-full scale-x-125 scale-y-105 object-cover blur-[20px]"
              />
            </div>
          )}
          {/* 加载状态显示 */}
          {isLoading && (
            <div className={cn('absolute inset-0 z-[-1] animate-pulse bg-gray-300/20', borderRadiusClassName)} />
          )}
          {isMobile ? (
            <img
              src={coverUri}
              alt={alt}
              loading="eager"
              className={cn('w-full object-contain', isLoading && 'opacity-0', borderRadiusClassName, imageClassName)}
              style={{
                aspectRatio,
              }}
              crossOrigin="anonymous"
              onLoad={handleMediaLoaded}
            />
          ) : (
            <div
              className={cn(
                'h-full w-full',
                !isLoading && 'opacity-100',
                'overflow-hidden transition-opacity duration-300',
                borderRadiusClassName,
              )}
            >
              <video
                ref={videoRef}
                preload="none"
                className={cn('relative h-full w-full', videoClassName || 'scale-[1.05] object-contain')}
                crossOrigin="anonymous"
                muted={muted}
                loop
                playsInline
                webkit-playsinline="true"
                x5-playsinline="true"
                src={uri}
                onLoadedData={handleMediaLoaded}
                onTimeUpdate={handleTimeUpdate}
                poster={coverUri}
              />
            </div>
          )}
          {/* 子元素，用于自定义内容 */}
          {children}
          {showLoading && (
            <div className="absolute inset-0 flex items-center justify-center bg-system-black48">
              <Loading className="h-4 w-4 animate-spin" />
            </div>
          )}
        </div>

        {footer}
      </div>
    );
  },
);

HoverPlayMedia.displayName = 'HoverPlayMedia';

export default HoverPlayMedia;
